import React, { useState, useEffect } from 'react';
import { useToast } from './Toast';
import { geocodeSearch } from '../utils/api';
import InventoryWizard from './InventoryWizard';
import { resolveCoverImage } from '../utils/propertyCovers';
import { getNeighborhoodsForCity, OTHER_NEIGHBORHOOD } from '../utils/saudiNeighborhoods';
import SearchableNeighborhoodSelect from './SearchableNeighborhoodSelect';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  FaBuilding, 
  FaPlus, 
  FaEdit, 
  FaTrash,
  FaTimes,
  FaMapMarkerAlt,
  FaHome,
  FaStore,
  FaCity,
  FaMap,
  FaCheck
} from 'react-icons/fa';

// Fix Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom orange marker
const orangeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (process.env.NODE_ENV === 'production') {
    return '';
  }
  return 'http://localhost:5001';
};
const API_BASE = getApiBaseUrl();

// Map click handler component
const LocationMarker = ({ position, setPosition }) => {
  useMapEvents({
    click(e) {
      const newPos = [e.latlng.lat, e.latlng.lng];
      console.log('Map clicked at:', newPos);
      setPosition(newPos);
    },
  });

  return position ? <Marker position={position} icon={orangeIcon} /> : null;
};

// Component to update map view when center changes
const MapController = ({ center, zoom }) => {
  const map = useMap();

  useEffect(() => {
    if (center && center.length === 2 && map) {
      // Force map size recalculation and then set view
      map.invalidateSize(true);
      setTimeout(() => {
        map.setView(center, zoom, { animate: true });
      }, 50);
    }
  }, [center, zoom, map]);

  // Force size recalculation on mount
  useEffect(() => {
    if (map) {
      const timer = setTimeout(() => {
        map.invalidateSize(true);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [map]);

  return null;
};

// Fallback tile layer component
const TileLayerWithFallback = () => {
  const [tileError, setTileError] = useState(false);
  
  return (
    <>
      {!tileError ? (
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={19}
          subdomains={['a', 'b', 'c']}
          tileSize={256}
          zoomOffset={0}
          crossOrigin="anonymous"
          referrerPolicy="no-referrer-when-downgrade"
          eventHandlers={{
            tileerror: () => {
              console.warn('Primary tile layer failed, switching to fallback');
              setTileError(true);
            }
          }}
        />
      ) : (
        <TileLayer
          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          maxZoom={19}
          subdomains={['a', 'b', 'c', 'd']}
        />
      )}
    </>
  );
};

/** Saudi cities in the property form dropdown (Arabic values). Geocoders often return English — map when possible. */
const PROPERTY_CITY_VALUES = [
  'الرياض', 'جدة', 'الدمام', 'مكة', 'المدينة', 'الخبر', 'الظهران', 'تبوك', 'بريدة',
  'خميس مشيط', 'أبها', 'الطائف', 'حائل', 'نجران', 'جازان', 'ينبع', 'الجبيل', 'الأحساء'
];

const EN_CITY_TO_AR = [
  [/riyadh|ar riyad|الرياض/i, 'الرياض'],
  [/jeddah|jiddah|جدة/i, 'جدة'],
  [/dammam|الدمام/i, 'الدمام'],
  [/makkah|mecca|مكة(?! المكرمة)/i, 'مكة'],
  [/makkah al mukarramah|مكة المكرمة/i, 'مكة'],
  [/madinah|medina|المدينة/i, 'المدينة'],
  [/khobar|al khobar|الخبر/i, 'الخبر'],
  [/dhahran|الظهران/i, 'الظهران'],
  [/tabuk|تبوك/i, 'تبوك'],
  [/buraidah|buraydah|بريدة/i, 'بريدة'],
  [/khamis mushait|خميس مشيط/i, 'خميس مشيط'],
  [/abha|أبها/i, 'أبها'],
  [/taif|طائف|at taif/i, 'الطائف'],
  [/hail|حائل/i, 'حائل'],
  [/najran|نجران/i, 'نجران'],
  [/jazan|jizan|جازان/i, 'جازان'],
  [/yanbu|ينبع/i, 'ينبع'],
  [/jubail|jubayl|الجبيل/i, 'الجبيل'],
  [/hsa|ahsa|al ahsa|al hasa|الأحساء|hofuf/i, 'الأحساء']
];

function normalizeCityFromGeocode(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  if (PROPERTY_CITY_VALUES.includes(s)) return s;
  for (const [re, ar] of EN_CITY_TO_AR) {
    if (re.test(s)) return ar;
  }
  return s;
}

function fmtCoord(n) {
  const x = Number(n);
  return Number.isFinite(x) ? x.toFixed(6) : '';
}

const Properties = ({ language = 'ar', userRole }) => {
  const toast = useToast();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showInventoryWizard, setShowInventoryWizard] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showMapModal, setShowMapModal] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [mapPosition, setMapPosition] = useState(null);
  const [mapCenter, setMapCenter] = useState([24.7136, 46.6753]); // Riyadh default
  const [mapZoom, setMapZoom] = useState(6);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = React.useRef(null);

  // Delay map render to ensure DOM is ready
  useEffect(() => {
    if (showMapModal) {
      // Give more time for map to properly initialize
      const timer = setTimeout(() => {
        setMapReady(true);
        console.log('Map ready state set to true');
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setMapReady(false);
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [showMapModal]);

  // Search via API (Saudi-biased Nominatim + Photon; short codes e.g. RRRA6565)
  const searchLocation = async (query) => {
    const q = String(query || '').trim();
    if (!q || q.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const data = await geocodeSearch(q, language === 'ar' ? 'ar' : 'en');
      setSearchResults(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      searchLocation(value);
    }, 400);
  };

  // Select a search result
  const selectSearchResult = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setMapPosition([lat, lng]);
    setMapCenter([lat, lng]);
    setMapZoom(16);
    setSearchResults([]);
    const label = String(result.display_name || '').split(',')[0] || '';
    setSearchQuery(label.trim());
  };
  
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    district: '',
    districtOther: '',
    property_type: 'residential',
    total_units: '',
    notes: '',
    cover_image_url: '',
    latitude: null,
    longitude: null
  });

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`
  });

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/properties`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setProperties(data);
      }
    } catch (error) {
      console.error('Error loading properties:', error);
      toast.error(language === 'ar' ? 'فشل تحميل العقارات' : 'Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      city: '',
      district: '',
      districtOther: '',
      property_type: 'residential',
      total_units: '',
      notes: '',
      cover_image_url: '',
      latitude: null,
      longitude: null
    });
    setMapPosition(null);
    setEditingProperty(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (property) => {
    const la = property.latitude != null && property.latitude !== '' ? Number(property.latitude) : null;
    const lo = property.longitude != null && property.longitude !== '' ? Number(property.longitude) : null;
    const city = property.city || '';
    const district = property.district || '';
    const known = getNeighborhoodsForCity(city).some((n) => n.value === district);
    setFormData({
      name: property.name || '',
      city,
      district: district && !known ? OTHER_NEIGHBORHOOD : district,
      districtOther: district && !known ? district : '',
      property_type: property.property_type || 'residential',
      total_units: property.total_units || '',
      notes: property.notes || '',
      cover_image_url: property.cover_image_url || '',
      latitude: Number.isFinite(la) ? la : null,
      longitude: Number.isFinite(lo) ? lo : null
    });
    if (Number.isFinite(la) && Number.isFinite(lo)) {
      setMapPosition([la, lo]);
    } else {
      setMapPosition(null);
    }
    setEditingProperty(property);
    setShowModal(true);
  };

  // Reverse geocode to get city/district from coordinates (always persist lat/lng)
  const reverseGeocode = async (lat, lng) => {
    const latNum = Number(lat);
    const lngNum = Number(lng);
    console.log('Reverse geocoding for:', latNum, lngNum);
    setIsLoadingLocation(true);
    try {
      const acceptLang = language === 'ar' ? 'ar,en' : 'en,ar';
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latNum}&lon=${lngNum}&accept-language=${encodeURIComponent(acceptLang)}`
      );
      const data = await response.json();
      console.log('Reverse geocoding response:', data);

      let cityRaw = '';
      let district = '';
      if (data && data.address) {
        const address = data.address;
        cityRaw =
          address.city ||
          address.town ||
          address.municipality ||
          address.county ||
          address.state_district ||
          address.state ||
          '';
        district =
          address.suburb ||
          address.neighbourhood ||
          address.quarter ||
          address.city_district ||
          address.district ||
          address.hamlet ||
          '';
      }

      const cityNormalized = cityRaw ? normalizeCityFromGeocode(cityRaw) : '';
      const cityForDistrict = cityNormalized || '';
      const known = getNeighborhoodsForCity(cityForDistrict);
      const districtTrim = String(district || '').trim();
      const match = known.find((n) => n.value === districtTrim || n.labelAr === districtTrim);
      const districtValue = !districtTrim
        ? ''
        : match
          ? match.value
          : OTHER_NEIGHBORHOOD;

      setFormData((prev) => ({
        ...prev,
        city: cityNormalized || prev.city,
        district: districtValue || prev.district,
        districtOther: districtValue === OTHER_NEIGHBORHOOD ? districtTrim : '',
        latitude: latNum,
        longitude: lngNum
      }));

      toast.success(language === 'ar' ? 'تم تحديد الموقع' : 'Location set');
    } catch (error) {
      console.error('Geocoding error:', error);
      setFormData((prev) => ({
        ...prev,
        latitude: latNum,
        longitude: lngNum
      }));
      toast.success(language === 'ar' ? 'تم حفظ الإحداثيات' : 'Coordinates saved');
    } finally {
      setIsLoadingLocation(false);
    }
  };

  // Handle map location confirmation
  const handleMapConfirm = () => {
    console.log('Map confirm clicked, mapPosition:', mapPosition);
    if (mapPosition && mapPosition.length === 2) {
      console.log('Confirming position:', mapPosition[0], mapPosition[1]);
      reverseGeocode(mapPosition[0], mapPosition[1]);
      setShowMapModal(false);
    } else {
      console.log('No valid position selected');
      toast.error(language === 'ar' ? 'الرجاء اختيار موقع على الخريطة' : 'Please select a location on the map');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error(language === 'ar' ? 'اسم العقار مطلوب' : 'Property name is required');
      return;
    }

    const districtResolved =
      formData.district === OTHER_NEIGHBORHOOD
        ? (formData.districtOther || '').trim()
        : (formData.district || '').trim();

    if (formData.city && !districtResolved) {
      toast.error(language === 'ar' ? 'اختر الحي' : 'Select neighborhood');
      return;
    }

    const payload = {
      ...formData,
      district: districtResolved || null,
      cover_image_url: formData.cover_image_url || null,
    };
    delete payload.districtOther;

    try {
      const url = editingProperty 
        ? `${API_BASE}/api/properties/${editingProperty.id}`
        : `${API_BASE}/api/properties`;
      
      const response = await fetch(url, {
        method: editingProperty ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(
          editingProperty
            ? (language === 'ar' ? 'تم تحديث العقار' : 'Property updated')
            : (language === 'ar' ? 'تم إضافة العقار' : 'Property added')
        );
        setShowModal(false);
        resetForm();
        loadProperties();
      } else {
        const data = await response.json();
        toast.error(data.error || (language === 'ar' ? 'فشلت العملية' : 'Operation failed'));
      }
    } catch (error) {
      console.error('Error saving property:', error);
      toast.error(language === 'ar' ? 'حدث خطأ' : 'An error occurred');
    }
  };

  const handleDelete = async (propertyId) => {
    try {
      const response = await fetch(`${API_BASE}/api/properties/${propertyId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        const unlinked = data.unlinked_units || 0;
        toast.success(
          unlinked > 0
            ? language === 'ar'
              ? `تم حذف العقار. ${unlinked} وحدة بقيت بدون عقار مرتبط.`
              : `Property deleted. ${unlinked} unit(s) were kept but unlinked from this property.`
            : language === 'ar'
              ? 'تم حذف العقار'
              : 'Property deleted'
        );
        setShowDeleteConfirm(null);
        loadProperties();
      } else {
        const data = await response.json();
        toast.error(data.error || (language === 'ar' ? 'فشل الحذف' : 'Delete failed'));
      }
    } catch (error) {
      console.error('Error deleting property:', error);
      toast.error(language === 'ar' ? 'حدث خطأ' : 'An error occurred');
    }
  };

  const getPropertyTypeIcon = (type) => {
    switch (type) {
      case 'commercial': return FaStore;
      case 'mixed': return FaCity;
      default: return FaHome;
    }
  };

  const getPropertyTypeLabel = (type) => {
    const labels = {
      residential: language === 'ar' ? 'سكني' : 'Residential',
      commercial: language === 'ar' ? 'تجاري' : 'Commercial',
      mixed: language === 'ar' ? 'مختلط' : 'Mixed'
    };
    return labels[type] || labels.residential;
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
        {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', direction: language === 'ar' ? 'rtl' : 'ltr' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div>
          <h1 style={{ 
            margin: 0, 
            fontSize: '1.75rem', 
            fontWeight: '600', 
            color: '#1f2937',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <FaBuilding style={{ color: '#f97316' }} />
            {language === 'ar' ? 'العقارات' : 'Properties'}
          </h1>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '0.9rem' }}>
            {language === 'ar' 
              ? `${properties.length} عقار`
              : `${properties.length} ${properties.length === 1 ? 'property' : 'properties'}`}
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowInventoryWizard(true)}
            style={{
              padding: '12px 20px',
              background: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '0.95rem',
              boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)',
            }}
          >
            <FaPlus />
            {language === 'ar' ? 'إضافة (معالج)' : 'Add (wizard)'}
          </button>
          <button
            onClick={openAddModal}
            style={{
              padding: '12px 16px',
              background: '#fff',
              color: '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '0.875rem',
            }}
          >
            {language === 'ar' ? 'عقار سريع' : 'Quick property'}
          </button>
        </div>
      </div>

      {/* Empty State */}
      {properties.length === 0 ? (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '60px 40px',
          textAlign: 'center',
          border: '2px dashed #e5e7eb'
        }}>
          <FaBuilding style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '16px' }} />
          <h3 style={{ margin: '0 0 8px', color: '#6b7280', fontWeight: '500' }}>
            {language === 'ar' ? 'لا توجد عقارات بعد' : 'No properties yet'}
          </h3>
          <p style={{ margin: '0 0 20px', color: '#9ca3af', fontSize: '0.9rem' }}>
            {language === 'ar' 
              ? 'أضف عقارك الأول للبدء في إدارة الوحدات'
              : 'Add your first property to start managing units'}
          </p>
          <button
            onClick={() => setShowInventoryWizard(true)}
            style={{
              padding: '12px 24px',
              background: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '500',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FaPlus />
            {language === 'ar' ? 'إضافة عقار' : 'Add Property'}
          </button>
        </div>
      ) : (
        /* Properties Grid */
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', 
          gap: '20px' 
        }}>
          {properties.map(property => {
            const TypeIcon = getPropertyTypeIcon(property.property_type);
            return (
              <div
                key={property.id}
                style={{
                  background: 'white',
                  borderRadius: '14px',
                  padding: 0,
                  overflow: 'hidden',
                  border: '1px solid #e5e7eb',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
                  e.currentTarget.style.borderColor = '#f97316';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                <img
                  src={resolveCoverImage({
                    coverImageUrl: property.cover_image_url,
                    propertyType: property.property_type,
                  })}
                  alt=""
                  style={{ width: '100%', height: 150, objectFit: 'cover', display: 'block', background: '#f5f5f4' }}
                />
                <div style={{ padding: '16px 20px 20px' }}>
                {/* Property Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <TypeIcon style={{ fontSize: '22px', color: '#f97316' }} />
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', color: '#1f2937' }}>
                        {property.name}
                      </h3>
                      <span style={{ 
                        fontSize: '0.75rem', 
                        color: '#f97316',
                        background: '#fff7ed',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontWeight: '500'
                      }}>
                        {getPropertyTypeLabel(property.property_type)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); openEditModal(property); }}
                      style={{
                        padding: '8px',
                        background: '#f3f4f6',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        color: '#6b7280',
                        transition: 'all 0.15s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f97316';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#f3f4f6';
                        e.currentTarget.style.color = '#6b7280';
                      }}
                      title={language === 'ar' ? 'تعديل' : 'Edit'}
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(property.id); }}
                      style={{
                        padding: '8px',
                        background: '#f3f4f6',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        color: '#6b7280',
                        transition: 'all 0.15s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#ef4444';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#f3f4f6';
                        e.currentTarget.style.color = '#6b7280';
                      }}
                      title={language === 'ar' ? 'حذف' : 'Delete'}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>

                {/* Property Details */}
                {(property.city || property.district) && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '6px',
                    color: '#6b7280',
                    fontSize: '0.85rem',
                    marginBottom: '12px'
                  }}>
                    <FaMapMarkerAlt style={{ color: '#9ca3af', fontSize: '12px' }} />
                    <span>
                      {[property.district, property.city].filter(Boolean).join(' • ')}
                    </span>
                  </div>
                )}

                {/* Units Count */}
                {property.total_units && (
                  <div style={{
                    background: '#f9fafb',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <FaBuilding style={{ color: '#9ca3af', fontSize: '14px' }} />
                    <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                      {property.total_units} {language === 'ar' ? 'وحدة' : 'units'}
                    </span>
                  </div>
                )}

                {/* Notes */}
                {property.notes && (
                  <p style={{ 
                    margin: '12px 0 0', 
                    color: '#9ca3af', 
                    fontSize: '0.8rem',
                    fontStyle: 'italic',
                    lineHeight: '1.4'
                  }}>
                    {property.notes}
                  </p>
                )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1001,
            padding: '20px'
          }}
          onClick={() => { setShowModal(false); resetForm(); }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '20px',
              width: '100%',
              maxWidth: '560px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ 
              padding: '24px 28px',
              borderBottom: '1px solid #f3f4f6',
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              background: 'linear-gradient(to bottom, #ffffff, #fefefe)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FaBuilding style={{ color: '#f97316', fontSize: '18px' }} />
                </div>
                <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#1f2937', fontWeight: '600' }}>
                  {editingProperty 
                    ? (language === 'ar' ? 'تعديل العقار' : 'Edit Property')
                    : (language === 'ar' ? 'إضافة عقار جديد' : 'Add New Property')}
                </h2>
              </div>
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                style={{
                  background: '#f3f4f6',
                  border: 'none',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#6b7280',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#fee2e2';
                  e.currentTarget.style.color = '#dc2626';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.color = '#6b7280';
                }}
              >
                <FaTimes />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} style={{ padding: '28px 28px 24px' }}>
              {/* Basic Information Section */}
              <div style={{ 
                marginBottom: '28px',
                paddingBottom: '24px',
                borderBottom: '1px solid #f3f4f6'
              }}>
                <div style={{ 
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: '#9ca3af',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '16px'
                }}>
                  {language === 'ar' ? 'المعلومات الأساسية' : 'Basic Information'}
                </div>

                {/* Property Name */}
                <div style={{ marginBottom: '18px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '10px', 
                    fontWeight: '600', 
                    color: '#1f2937',
                    fontSize: '0.95rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    {language === 'ar' ? 'اسم العقار' : 'Property Name'} 
                    <span style={{ 
                      color: '#ef4444',
                      fontSize: '0.85rem',
                      background: '#fee2e2',
                      padding: '1px 6px',
                      borderRadius: '4px'
                    }}>
                      *
                    </span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={language === 'ar' ? 'مثال: برج الياسمين' : 'e.g. Al Yasmin Tower'}
                    style={{
                      width: '100%',
                      padding: '16px 18px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      fontSize: '1.05rem',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box',
                      background: '#fafafa'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#f97316';
                      e.target.style.background = 'white';
                      e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.background = '#fafafa';
                      e.target.style.boxShadow = 'none';
                    }}
                    autoFocus
                  />
                </div>
              </div>

              {/* Property Type */}
              <div style={{ marginBottom: '28px', paddingBottom: '24px', borderBottom: '1px solid #f3f4f6' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '12px', 
                  fontWeight: '600', 
                  color: '#1f2937',
                  fontSize: '0.95rem'
                }}>
                  {language === 'ar' ? 'نوع العقار' : 'Property Type'}
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  {[
                    { value: 'residential', label: language === 'ar' ? 'سكني' : 'Residential', icon: FaHome },
                    { value: 'commercial', label: language === 'ar' ? 'تجاري' : 'Commercial', icon: FaStore },
                    { value: 'mixed', label: language === 'ar' ? 'مختلط' : 'Mixed', icon: FaCity }
                  ].map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, property_type: type.value })}
                      style={{
                        padding: '16px 12px',
                        border: formData.property_type === type.value 
                          ? '2px solid #f97316' 
                          : '2px solid #e5e7eb',
                        borderRadius: '12px',
                        background: formData.property_type === type.value 
                          ? 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)' 
                          : 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.2s ease',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => {
                        if (formData.property_type !== type.value) {
                          e.currentTarget.style.borderColor = '#d1d5db';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (formData.property_type !== type.value) {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }
                      }}
                    >
                      {formData.property_type === type.value && (
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          width: '18px',
                          height: '18px',
                          borderRadius: '50%',
                          background: '#f97316',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <FaCheck style={{ color: 'white', fontSize: '10px' }} />
                        </div>
                      )}
                      <type.icon style={{ 
                        fontSize: '24px', 
                        color: formData.property_type === type.value ? '#f97316' : '#9ca3af' 
                      }} />
                      <span style={{ 
                        fontSize: '0.85rem', 
                        fontWeight: '600',
                        color: formData.property_type === type.value ? '#f97316' : '#6b7280'
                      }}>
                        {type.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Location Section */}
              <div style={{ marginBottom: '28px', paddingBottom: '24px', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '14px'
                }}>
                  <label style={{ 
                    fontWeight: '600', 
                    color: '#1f2937', 
                    fontSize: '0.95rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <FaMapMarkerAlt style={{ color: '#f97316', fontSize: '14px' }} />
                    {language === 'ar' ? 'الموقع' : 'Location'}
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowMapModal(true)}
                    style={{
                      padding: '8px 16px',
                      background: (formData.latitude != null && formData.latitude !== '') ? 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)' : 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
                      color: (formData.latitude != null && formData.latitude !== '') ? '#166534' : '#ea580c',
                      border: (formData.latitude != null && formData.latitude !== '') ? '2px solid #86efac' : '2px solid #fed7aa',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                    }}
                  >
                    {(formData.latitude != null && formData.latitude !== '') ? <FaCheck /> : <FaMap />}
                    {(formData.latitude != null && formData.latitude !== '')
                      ? (language === 'ar' ? 'تم التحديد' : 'Set')
                      : (language === 'ar' ? 'اختر من الخريطة' : 'Pick on Map')}
                  </button>
                </div>

                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '14px',
                  alignItems: 'stretch'
                }}>
                  {/* City */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '0.8rem',
                      fontWeight: '500',
                      color: '#6b7280'
                    }}>
                      {language === 'ar' ? 'المدينة' : 'City'}
                    </label>
                    <select
                      value={formData.city}
                      onChange={(e) => {
                        const city = e.target.value;
                        const nextOptions = getNeighborhoodsForCity(city);
                        const keep =
                          formData.district === OTHER_NEIGHBORHOOD ||
                          nextOptions.some((n) => n.value === formData.district);
                        setFormData({
                          ...formData,
                          city,
                          district: keep ? formData.district : '',
                          districtOther: keep && formData.district === OTHER_NEIGHBORHOOD ? formData.districtOther : '',
                        });
                      }}
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        fontSize: '0.95rem',
                        background: '#fafafa',
                        boxSizing: 'border-box',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        height: '52px'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#f97316';
                        e.target.style.background = 'white';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.background = '#fafafa';
                      }}
                    >
                      <option value="">{language === 'ar' ? 'اختر...' : 'Select...'}</option>
                      <option value="الرياض">الرياض - Riyadh</option>
                      <option value="جدة">جدة - Jeddah</option>
                      <option value="الدمام">الدمام - Dammam</option>
                      <option value="مكة">مكة - Makkah</option>
                      <option value="المدينة">المدينة - Madinah</option>
                      <option value="الخبر">الخبر - Khobar</option>
                      <option value="الظهران">الظهران - Dhahran</option>
                      <option value="تبوك">تبوك - Tabuk</option>
                      <option value="بريدة">بريدة - Buraidah</option>
                      <option value="خميس مشيط">خميس مشيط - Khamis Mushait</option>
                      <option value="أبها">أبها - Abha</option>
                      <option value="الطائف">الطائف - Taif</option>
                      <option value="حائل">حائل - Hail</option>
                      <option value="نجران">نجران - Najran</option>
                      <option value="جازان">جازان - Jazan</option>
                      <option value="ينبع">ينبع - Yanbu</option>
                      <option value="الجبيل">الجبيل - Jubail</option>
                      <option value="الأحساء">الأحساء - Al Ahsa</option>
                      {formData.city &&
                        !PROPERTY_CITY_VALUES.includes(formData.city) && (
                        <option value={formData.city}>{formData.city}</option>
                      )}
                    </select>
                  </div>
                  
                  {/* District / Neighborhood */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '0.8rem',
                      fontWeight: '500',
                      color: '#6b7280'
                    }}>
                      {language === 'ar' ? 'الحي' : 'Neighborhood'}
                    </label>
                    <SearchableNeighborhoodSelect
                      language={language}
                      city={formData.city}
                      options={getNeighborhoodsForCity(formData.city)}
                      value={formData.district}
                      disabled={!formData.city}
                      onChange={(district) =>
                        setFormData({
                          ...formData,
                          district,
                          districtOther: district === OTHER_NEIGHBORHOOD ? formData.districtOther : '',
                        })
                      }
                    />
                    {formData.district === OTHER_NEIGHBORHOOD && (
                      <input
                        type="text"
                        value={formData.districtOther}
                        onChange={(e) => setFormData({ ...formData, districtOther: e.target.value })}
                        placeholder={language === 'ar' ? 'اسم الحي' : 'Neighborhood name'}
                        style={{
                          width: '100%',
                          marginTop: 8,
                          padding: '12px 14px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          fontSize: '0.95rem',
                          boxSizing: 'border-box',
                        }}
                      />
                    )}
                  </div>
                </div>
                
                {/* Show coordinates if set */}
                {formData.latitude != null && formData.latitude !== '' && fmtCoord(formData.latitude) && (
                  <div style={{ 
                    marginTop: '12px', 
                    padding: '10px 14px',
                    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                    border: '1px solid #bbf7d0',
                    borderRadius: '10px',
                    fontSize: '0.8rem',
                    color: '#166534',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: '500'
                  }}>
                    <div style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: '#16a34a',
                      animation: 'pulse 2s ease-in-out infinite'
                    }} />
                    GPS: {fmtCoord(formData.latitude)}, {fmtCoord(formData.longitude)}
                  </div>
                )}
              </div>

              {/* Additional Details Section */}
              <div style={{ marginBottom: '28px' }}>
                <div style={{ 
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: '#9ca3af',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '16px'
                }}>
                  {language === 'ar' ? 'تفاصيل إضافية (اختياري)' : 'Additional Details (Optional)'}
                </div>

                <div style={{ marginBottom: '18px' }}>
                  <img
                    src={resolveCoverImage({
                      coverImageUrl: formData.cover_image_url,
                      propertyType: formData.property_type,
                    })}
                    alt=""
                    style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 12, display: 'block', marginBottom: 10, background: '#f5f5f4' }}
                  />
                  <label style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 14px',
                    borderRadius: 10,
                    border: '1px solid #e5e7eb',
                    background: '#fafafa',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#374151',
                  }}>
                    {language === 'ar' ? 'رفع صورة' : 'Upload photo'}
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 2_500_000) {
                          toast.error(language === 'ar' ? 'الصورة كبيرة جداً' : 'Image too large');
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = () => setFormData((prev) => ({ ...prev, cover_image_url: String(reader.result || '') }));
                        reader.readAsDataURL(file);
                      }}
                    />
                  </label>
                  {formData.cover_image_url ? (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, cover_image_url: '' })}
                      style={{ marginInlineStart: 10, border: 'none', background: 'none', color: '#ea580c', cursor: 'pointer', fontSize: '0.85rem' }}
                    >
                      {language === 'ar' ? 'افتراضي حسب النوع' : 'Use type default'}
                    </button>
                  ) : (
                    <span style={{ marginInlineStart: 10, fontSize: '0.78rem', color: '#9ca3af' }}>
                      {language === 'ar' ? 'بدون رفع تُعرض صورة افتراضية' : 'Default type image until you upload'}
                    </span>
                  )}
                </div>

                {/* Total Units */}
                <div style={{ marginBottom: '18px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '10px', 
                    fontWeight: '600', 
                    color: '#1f2937',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <FaBuilding style={{ fontSize: '12px', color: '#9ca3af' }} />
                    {language === 'ar' ? 'عدد الوحدات' : 'Total Units'}
                  </label>
                  <input
                    type="number"
                    value={formData.total_units}
                    onChange={(e) => setFormData({ ...formData, total_units: e.target.value })}
                    placeholder={language === 'ar' ? 'مثال: 24' : 'e.g. 24'}
                    min="0"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      fontSize: '1rem',
                      background: '#fafafa',
                      boxSizing: 'border-box',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#f97316';
                      e.target.style.background = 'white';
                      e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.background = '#fafafa';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Notes */}
                <div style={{ marginBottom: '0' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '10px', 
                    fontWeight: '600', 
                    color: '#1f2937',
                    fontSize: '0.9rem'
                  }}>
                    {language === 'ar' ? 'ملاحظات' : 'Notes'}
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    placeholder={language === 'ar' ? 'أي ملاحظات أو تفاصيل إضافية...' : 'Any additional notes or details...'}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      fontSize: '0.95rem',
                      background: '#fafafa',
                      resize: 'vertical',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit',
                      lineHeight: '1.6',
                      transition: 'all 0.2s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#f97316';
                      e.target.style.background = 'white';
                      e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.background = '#fafafa';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>

              {/* Submit Buttons */}
              <div style={{ 
                display: 'flex', 
                gap: '12px',
                paddingTop: '24px',
                borderTop: '1px solid #f3f4f6'
              }}>
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  style={{
                    flex: 1,
                    padding: '16px',
                    background: '#f9fafb',
                    color: '#374151',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f3f4f6';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f9fafb';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1.5,
                    padding: '16px',
                    background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontWeight: '700',
                    fontSize: '0.95rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(249, 115, 22, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.3)';
                  }}
                >
                  {editingProperty 
                    ? (
                      <>
                        <FaCheck style={{ fontSize: '14px' }} />
                        {language === 'ar' ? 'حفظ التغييرات' : 'Save Changes'}
                      </>
                    )
                    : (
                      <>
                        <FaPlus style={{ fontSize: '14px' }} />
                        {language === 'ar' ? 'إضافة العقار' : 'Add Property'}
                      </>
                    )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1002
          }}
          onClick={() => setShowDeleteConfirm(null)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              width: '90%',
              maxWidth: '360px',
              textAlign: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: '#fef2f2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <FaTrash style={{ fontSize: '24px', color: '#ef4444' }} />
            </div>
            <h3 style={{ margin: '0 0 8px', color: '#1f2937', fontSize: '1.1rem' }}>
              {language === 'ar' ? 'حذف العقار؟' : 'Delete Property?'}
            </h3>
            <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: '0.9rem' }}>
              {language === 'ar' 
                ? 'سيتم حذف العقار. الوحدات المرتبطة به تبقى في النظام لكن بدون عقار (يمكن ربطها لاحقاً).'
                : 'The property will be deleted. Linked units stay in the system but will be unlinked (you can assign them to another property later).'}
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                {language === 'ar' ? 'حذف' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Map Picker Modal */}
      {showMapModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1100,
            padding: '20px'
          }}
          onClick={() => setShowMapModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '700px',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ 
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaMapMarkerAlt style={{ color: '#f97316' }} />
                {language === 'ar' ? 'اختر موقع العقار' : 'Select Property Location'}
              </h3>
              <button
                onClick={() => setShowMapModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px'
                }}
              >
                <FaTimes />
              </button>
            </div>

            {/* Search Bar */}
            <div style={{ 
              padding: '12px 20px', 
              background: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
              position: 'relative'
            }}>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder={language === 'ar' 
                    ? '🔍 عنوان، حي، مدينة، أو رمز قصير (مثل RRRA6565)'
                    : '🔍 Address, district, city, or short code (e.g. RRRA6565)'}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    paddingRight: isSearching ? '40px' : '16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '10px',
                    fontSize: '0.95rem',
                    boxSizing: 'border-box',
                    direction: language === 'ar' ? 'rtl' : 'ltr'
                  }}
                />
                {isSearching && (
                  <div style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '20px',
                    height: '20px',
                    border: '2px solid #e5e7eb',
                    borderTop: '2px solid #f97316',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                )}
              </div>

              {/* Search Results Dropdown */}
              {searchResults.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '20px',
                  right: '20px',
                  background: 'white',
                  borderRadius: '10px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  maxHeight: '200px',
                  overflow: 'auto',
                  marginTop: '-8px'
                }}>
                  {searchResults.map((result, index) => (
                    <button
                      key={index}
                      onClick={() => selectSearchResult(result)}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        background: 'white',
                        textAlign: language === 'ar' ? 'right' : 'left',
                        cursor: 'pointer',
                        borderBottom: index < searchResults.length - 1 ? '1px solid #f3f4f6' : 'none',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '10px',
                        transition: 'background 0.15s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#fff7ed'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                    >
                      <FaMapMarkerAlt style={{ color: '#f97316', marginTop: '3px', flexShrink: 0 }} />
                      <span style={{ fontSize: '0.9rem', color: '#374151', lineHeight: '1.4' }}>
                        {result.display_name}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Instructions */}
            <div style={{ 
              padding: '10px 20px', 
              background: '#fff7ed',
              fontSize: '0.8rem',
              color: '#92400e',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>💡</span>
              {language === 'ar' 
                ? 'ابحث أو انقر على الخريطة لتحديد الموقع'
                : 'Search or click on the map to set location'}
            </div>

            {/* Map Container */}
            <div style={{ 
              height: '350px', 
              position: 'relative', 
              background: '#f3f4f6',
              borderRadius: '8px',
              overflow: 'hidden',
              border: '1px solid #e5e7eb'
            }}>
              {mapReady ? (
                <MapContainer
                  key="property-location-map"
                  center={mapCenter}
                  zoom={mapZoom}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    maxZoom={19}
                  />
                  <LocationMarker position={mapPosition} setPosition={setMapPosition} />
                  <MapController center={mapCenter} zoom={mapZoom} />
                </MapContainer>
              ) : (
                <div style={{ 
                  height: '100%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: '#6b7280'
                }}>
                  {language === 'ar' ? 'جاري تحميل الخريطة...' : 'Loading map...'}
                </div>
              )}
              
              {/* Loading overlay */}
              {isLoadingLocation && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255,255,255,0.8)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 1000
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      width: '40px', 
                      height: '40px', 
                      border: '3px solid #f3f4f6',
                      borderTop: '3px solid #f97316',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      margin: '0 auto 10px'
                    }} />
                    {language === 'ar' ? 'جاري تحديد العنوان...' : 'Getting address...'}
                  </div>
                </div>
              )}
            </div>

            {/* Selected location info */}
            {mapPosition && (
              <div style={{ 
                padding: '12px 20px', 
                background: '#f0fdf4',
                borderTop: '1px solid #dcfce7',
                fontSize: '0.85rem',
                color: '#166534',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <FaMapMarkerAlt />
                {language === 'ar' ? 'الإحداثيات:' : 'Coordinates:'} {mapPosition[0].toFixed(6)}, {mapPosition[1].toFixed(6)}
              </div>
            )}

            {/* Actions */}
            <div style={{ 
              padding: '16px 20px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowMapModal(false)}
                style={{
                  padding: '12px 20px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '0.95rem'
                }}
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
              <button
                onClick={handleMapConfirm}
                disabled={!mapPosition}
                style={{
                  padding: '12px 24px',
                  background: mapPosition ? '#f97316' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: mapPosition ? 'pointer' : 'not-allowed',
                  fontWeight: '600',
                  fontSize: '0.95rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <FaCheck />
                {language === 'ar' ? 'تأكيد الموقع' : 'Confirm Location'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }
      `}</style>

      {showInventoryWizard && (
        <InventoryWizard
          language={language}
          onClose={() => setShowInventoryWizard(false)}
          onSuccess={() => {
            setShowInventoryWizard(false);
            loadProperties();
            toast.success(language === 'ar' ? 'تمت الإضافة بنجاح' : 'Added successfully');
          }}
        />
      )}
    </div>
  );
};

export default Properties;
